import { useState, useEffect } from "react";
import { Ban, EyeOff, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Infringement } from "@/types/domain";

export type ActionKey = "block" | "ignore";

export interface InfringementActionsProps {
  infringement: Infringement;
  isBusy: boolean;
  onAction: (action: ActionKey) => void;
}

type Intent = "destructive" | "neutral" | "primary";

const ACTION_CONFIG: Array<{
  key: ActionKey;
  label: string;
  intent: Intent;
  icon: React.ElementType;
}> = [
  { key: "block", label: "Block", intent: "destructive", icon: Ban },
  { key: "ignore", label: "Ignore", intent: "neutral", icon: EyeOff },
];

export function InfringementActions({
  infringement,
  isBusy,
  onAction,
}: InfringementActionsProps) {
  const [confirming, setConfirming] = useState<ActionKey | null>(null);
  const [waitingAction, setWaitingAction] = useState<ActionKey | null>(null);

  // Reset confirmation state if the selected infringement changes
  useEffect(() => {
    setConfirming(null);
    setWaitingAction(null);
  }, [infringement.id]);

  // Clear waiting state if the status is successfully updated via WebSocket
  useEffect(() => {
    if (infringement.action_result) {
      setWaitingAction(null);
    }
  }, [infringement.action_result]);

  // Fallback: don't lock forever if the WebSocket update drops
  useEffect(() => {
    if (waitingAction) {
      const timer = setTimeout(() => setWaitingAction(null), 7000);
      return () => clearTimeout(timer);
    }
  }, [waitingAction]);

  const currentActive =
    infringement.action_result === "blocked"
      ? "block"
      : infringement.action_result === "ignored"
        ? "ignore"
        : null;

  const confirmingConfig = ACTION_CONFIG.find((c) => c.key === confirming);

  // Define colors based on intent
  const intentStyles = {
    destructive: {
      active: "bg-red-500/10 border-red-500/50 text-red-600 dark:bg-red-500/20 dark:border-red-400/50 dark:text-red-400",
      button: "hover:border-red-500/50 hover:text-red-600 hover:bg-red-500/10 dark:hover:border-red-400/50 dark:hover:text-red-400 dark:hover:bg-red-500/15",
    },
    neutral: {
      active: "bg-muted/10 border-muted/30 text-muted",
      button: "hover:border-border hover:bg-surface-elevated",
    },
    primary: {
      active: "bg-brand-cyan/10 border-brand-cyan/30 text-brand-cyan",
      button: "hover:border-brand-cyan/50 hover:text-brand-cyan hover:bg-brand-cyan/5",
    },
  };

  return (
    <div className="relative flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        {ACTION_CONFIG.map((a) => {
          const Icon = a.icon;
          const isActive = currentActive === a.key;
          const isSelected = confirming === a.key;
          const isWaiting = waitingAction === a.key;
          const styles = intentStyles[a.intent];

          if (isActive) {
            return (
              <div
                key={a.key}
                className={cn(
                  "flex items-center justify-center gap-2 py-2.5 rounded-md border text-xs font-semibold cursor-default opacity-90",
                  styles.active
                )}
                title={`${a.label} is currently active`}
              >
                <Icon className="h-4 w-4" />
                {a.label} (Active)
              </div>
            );
          }

          if (isWaiting) {
            return (
              <div
                key={a.key}
                className={cn(
                  "flex items-center justify-center gap-2 py-2.5 rounded-md border text-xs font-semibold cursor-wait opacity-80",
                  styles.active
                )}
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                {a.label}
              </div>
            );
          }

          return (
            <button
              key={a.key}
              onClick={() => setConfirming(isSelected ? null : a.key)}
              disabled={isBusy || (confirming !== null && !isSelected) || waitingAction !== null}
              className={cn(
                "flex items-center justify-center gap-2 py-2.5 rounded-md border bg-surface border-border text-secondary text-xs font-semibold transition-colors cursor-pointer",
                "disabled:opacity-40 disabled:cursor-not-allowed",
                isSelected ? styles.active : styles.button
              )}
            >
              <Icon className="h-4 w-4" />
              {a.label}
            </button>
          );
        })}
      </div>

      {/* Shared Confirmation Panel */}
      {confirmingConfig && (
        <div className="absolute top-full left-0 right-0 mt-3 z-10 flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-border/80 bg-surface shadow-xl ring-1 ring-black/5 animate-in slide-in-from-top-1 fade-in zoom-in-95 duration-150">
          {/* A small caret pointing up to make it look like a popover */}
          <div className={cn(
            "absolute -top-1.5 w-3 h-3 border-l border-t border-border/80 bg-surface rotate-45",
            confirming === "ignore" ? "right-6" : "left-6"
          )} />
          
          <span className="relative z-10 text-xs font-medium text-secondary">
            Confirm {confirmingConfig.label.toLowerCase()}?
          </span>
          
          <div className="relative z-10 flex items-center gap-2">
            <button
              onClick={() => setConfirming(null)}
              disabled={isBusy}
              className="flex items-center justify-center py-1.5 px-3 rounded-md hover:bg-surface-elevated text-muted/80 text-xs font-medium transition-colors disabled:opacity-50 cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setWaitingAction(confirmingConfig.key);
                onAction(confirmingConfig.key);
                setConfirming(null);
              }}
              disabled={isBusy}
              className="flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md bg-brand-cyan hover:bg-brand-cyan/90 border border-brand-cyan text-white text-xs font-semibold transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
            >
              <Check className="h-3.5 w-3.5" />
              Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
